import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { notEmpty } from "shared/lib/utils";

import { ContentHeadline } from "scmp-app/components/content/content-headline";
import { useImageUtilities } from "scmp-app/lib/hooks/image-utilities";
import type { itemBookmarkItemsContent$key } from "scmp-app/queries/__generated__/itemBookmarkItemsContent.graphql";

import { Item, StyledBaseImage, Title } from "./styles";

export type Props = {
  className?: string;
  reference: itemBookmarkItemsContent$key;
};

export const BookmarkItems: FunctionComponent<Props> = ({ className, reference }) => {
  const { getResizedImageUrl } = useImageUtilities();

  const content = useFragment(
    graphql`
      fragment itemBookmarkItemsContent on Content {
        ...entityLink
        ...contentHeadlineContent
        images {
          isSlideshow
          title
          url
        }
      }
    `,
    reference,
  );

  const renderMainImage = () => {
    if (!content || !content.images) return null;
    const images = content.images?.filter(img => notEmpty(img)) ?? [];
    const mainImage = images.find(img => !!img?.isSlideshow) ?? first(images);
    const source = mainImage?.url ? getResizedImageUrl(mainImage?.url, 80) : mainImage?.url;
    const alt = mainImage?.title ?? "";

    return (
      <StyledBaseImage
        $aspectRatio="1/1"
        $height="auto"
        $objectFit="cover"
        $width="100%"
        alt={alt}
        disableLinkingData
        src={source}
      />
    );
  };

  return (
    <BaseLinkContextProvider>
      <Item className={className} reference={content}>
        <Title>
          <ContentHeadline preferSocialHeadline={true} reference={content} />
        </Title>
        {renderMainImage()}
      </Item>
    </BaseLinkContextProvider>
  );
};

BookmarkItems.displayName = "BookmarkItems";
